import { defineStore } from 'pinia';
import { useCustomizerStore } from '@/stores/customizerStore';
import { nextTick } from 'vue';
import { Company } from '@/models/Company';
import axios from '@/utils/apiClient';

export const useCompanyStore = defineStore('companyStore', {
    state: () => ({
        companyID: getStoredCompanyID(),
        company: {} as Company,
        companies: [] as Company[],
        loading: false,
        initialized: false,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL || ''
    }),
    actions: {
        async initializeCompanies(companies: Company[]) {
            this.loading = true;
            try {
                this.companies = companies;
                const selectedCompany = companies.find(c => c.id === this.companyID) || companies[0];
                if (selectedCompany) {
                    this.company = selectedCompany;
                    this.companyID = selectedCompany.id;
                    persistCompanyID(this.companyID);
                    
                    if (selectedCompany.theme) {
                        const customizerStore = useCustomizerStore();
                        if (typeof customizerStore.setTheme === 'function') {
                            customizerStore.setTheme(selectedCompany.theme);
                        }
                    }
                }
                this.initialized = true;
            } catch (error) {
                console.error('Failed to initialize companies:', error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        
        async fetchCompanies() {
            this.loading = true;
            try {
                const response = await axios.get(`${this.apiBaseUrl}/api/companies`);
                await this.initializeCompanies(response.data);
                return response.data;
            } catch (error) {
                console.error('Failed to fetch companies:', error);
                throw new Error('Failed to load company data');
            } finally {
                this.loading = false;
            }
        },
        
        setListCompany(companies: Company[]) {
            const selectedCompany = companies.find(c => c.id === this.companyID) || companies[0];
            
            if (selectedCompany) {
                this.company = selectedCompany;
                this.companyID = selectedCompany.id;
                localStorage.setItem('companyID', this.companyID.toString());
            }
            
            this.companies = companies;
        },
        
        async setCompanyID(id: number) {
            if (this.companyID !== id) {
                const company = this.companies.find(c => c.id === id);
                if (company) {
                    this.company = company;
                    this.companyID = id;
                    persistCompanyID(id);
                    
                    const customizerStore = useCustomizerStore();
                    if (company.theme && typeof customizerStore.setTheme === 'function') {
                        customizerStore.setTheme(company.theme);
                    }
                    
                    await nextTick();
                    window.location.reload();
                } else {
                    console.warn(`Company with ID ${id} not found.`);
                }
            }
        },
    },
    getters: {
        getCompanyId: (state) => state.companyID,
        getCompanyName: (state) => {
            return state.company?.companyName || 
                   state.companies.find(c => c.id === state.companyID)?.companyName || 
                   'Unknown Company';
        },
        getCompanyLogo: (state) => {
            return state.company?.companyLogoPath || 
                   state.companies.find(c => c.id === state.companyID)?.companyLogoPath || 
                   '/images/default-logo.png';
        },
        getCompany: (state) => state.company,
        getCompanyList: (state) => state.companies,
    },
});

// Helper functions
function getStoredCompanyID(): number {
    const storedID = localStorage.getItem('companyID');
    const id = storedID ? parseInt(storedID) : 1;
    return isNaN(id) ? 1 : id;
}

function persistCompanyID(id: number): void {
    localStorage.setItem('companyID', id.toString());
}
